import { logEvent, Analytics, setUserId, setUserProperties } from 'firebase/analytics';
import { analyticsPromise } from '../utils/firebaseConfig';

let analytics: Analytics | null = null;

// Initialize analytics
analyticsPromise.then(a => {
  analytics = a;
});

// Helper to safely log events
const safeLogEvent = (eventName: string, params?: Record<string, any>) => {
  if (analytics) {
    logEvent(analytics, eventName, params);
  }
};

// ============================================
// User Identity
// ============================================

export const setAnalyticsUserId = (userId: string | null) => {
  if (analytics) {
    setUserId(analytics, userId);
  }
};

export const setAnalyticsUserProperties = (properties: Record<string, string>) => {
  if (analytics) {
    setUserProperties(analytics, properties);
  }
};

// ============================================
// Authentication Events
// ============================================

export const trackSignupCompleted = (params: {
  method: 'email' | 'google';
  has_name: boolean;
}) => {
  safeLogEvent('signup_completed', params);
};

export const trackLoginSuccess = (params: {
  method: 'email' | 'google';
}) => {
  safeLogEvent('login_success', params);
};

export const trackLoginFailed = (params: {
  method: 'email' | 'google';
  error_code: string;
}) => {
  safeLogEvent('login_failed', params);
};

export const trackPasswordResetRequested = () => {
  safeLogEvent('password_reset_requested');
};

// ============================================
// Event Creation & Editing
// ============================================

export const trackEventCreationStarted = () => {
  safeLogEvent('event_creation_started');
};

export const trackEventCreated = (params: {
  event_id: string;
  category: string;
  is_free: boolean;
  has_image: boolean;
  has_website: boolean;
  tag_count: number;
  city?: string;
}) => {
  safeLogEvent('event_created', params);
};

export const trackFirstEventCreated = (params: {
  event_id: string;
  days_since_signup: number;
}) => {
  safeLogEvent('first_event_created', params);
};

export const trackEventEdited = (params: {
  event_id: string;
  fields_changed: string[];
}) => {
  safeLogEvent('event_edited', {
    event_id: params.event_id,
    fields_changed: params.fields_changed.join(','),
    field_count: params.fields_changed.length,
  });
};

export const trackEventDeleted = (params: {
  event_id: string;
  category: string;
}) => {
  safeLogEvent('event_deleted', params);
};

// ============================================
// Event Discovery & Viewing
// ============================================

export const trackEventViewed = (params: {
  event_id: string;
  category: string;
  is_free: boolean;
  source?: 'search' | 'feed' | 'profile' | 'blog' | 'direct';
}) => {
  safeLogEvent('event_viewed', params);
};

export const trackEventSearch = (params: {
  query: string;
  results_count: number;
}) => {
  safeLogEvent('event_search', params);
};

export const trackFilterApplied = (params: {
  filter_type: 'free' | 'today' | 'weekend' | 'category' | 'city';
  filter_value?: string;
}) => {
  safeLogEvent('filter_applied', params);
};

export const trackFilterCleared = () => {
  safeLogEvent('filter_cleared');
};

// ============================================
// Event Engagement
// ============================================

export const trackEventInterested = (params: {
  event_id: string;
  category: string;
  is_interested: boolean;
  interested_count: number;
}) => {
  safeLogEvent('event_interested', params);
};

export const trackEventFavorited = (params: {
  event_id: string;
  category: string;
  is_favorited: boolean;
}) => {
  safeLogEvent('event_favorited', params);
};

export const trackEventWebsiteClicked = (params: {
  event_id: string;
  category: string;
}) => {
  safeLogEvent('event_website_clicked', params);
};

export const trackEventShared = (params: {
  event_id: string;
  share_method: string;
}) => {
  safeLogEvent('event_shared', params);
};

// ============================================
// Social Features
// ============================================

export const trackUserFollowed = (params: {
  target_user_id: string;
  is_following: boolean;
}) => {
  safeLogEvent('user_followed', params);
};

export const trackProfileViewed = (params: {
  profile_user_id: string;
  is_own_profile: boolean;
}) => {
  safeLogEvent('profile_viewed', params);
};

export const trackProfileUpdated = (params: {
  fields_updated: string[];
}) => {
  safeLogEvent('profile_updated', {
    fields_updated: params.fields_updated.join(','),
    field_count: params.fields_updated.length,
  });
};

// ============================================
// Comments
// ============================================

export const trackCommentPosted = (params: {
  target_type: 'event' | 'activity';
  target_id: string;
}) => {
  safeLogEvent('comment_posted', params);
};

export const trackCommentDeleted = (params: {
  target_type: 'event' | 'activity';
  target_id: string;
}) => {
  safeLogEvent('comment_deleted', params);
};

// ============================================
// Blog
// ============================================

export const trackBlogViewed = (params: {
  blog_id: string;
  blog_title: string;
}) => {
  safeLogEvent('blog_viewed', params);
};

export const trackBlogTagClicked = (params: {
  tag: string;
}) => {
  safeLogEvent('blog_tag_clicked', params);
};

// ============================================
// Feed
// ============================================

export const trackFeedViewed = (params: {
  activity_count: number;
}) => {
  safeLogEvent('feed_viewed', params);
};

// ============================================
// Moderation
// ============================================

export const trackReportSubmitted = (params: {
  target_type: 'event' | 'user' | 'comment';
  target_id: string;
  reason: string;
}) => {
  safeLogEvent('report_submitted', params);
};

// ============================================
// Page Views (automatic with Firebase, but custom for SPA)
// ============================================

export const trackPageView = (params: {
  page_name: string;
  page_path: string;
}) => {
  safeLogEvent('page_view', params);
};
